import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Code,
  Drawer,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconEye, IconSearch } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// Who changed what: every change made by staff or shop owners, recorded by the backend
// (GET /admin/audit-logs, newest first). Read-only.

const PAGE_SIZE = 50;

const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const formatTime = (iso) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const ROLE_COLOR = { SUPER_ADMIN: "red", ADMIN: "orange", MODERATOR: "blue", SHOP_OWNER: "grape" };

const StatusBadge = ({ status }) => {
  if (status == null) return <Badge color="gray" variant="light">-</Badge>;
  const ok = status < 400;
  return (
    <Badge color={ok ? "green" : "red"} variant="light">
      {ok ? "OK" : "Failed"} {status}
    </Badge>
  );
};

const JsonBlock = ({ label, value }) =>
  value == null ? null : (
    <Box>
      <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
        {label}
      </Text>
      <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(value, null, 2)}
      </Code>
    </Box>
  );

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ action: "", actorId: "", from: "", to: "" });
  const [applied, setApplied] = useState(filters);
  const [selected, setSelected] = useState(null);
  const { showError } = useNotification();

  const load = useCallback(
    async (before) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({ limit: PAGE_SIZE });
        if (applied.action.trim()) query.set("action", applied.action.trim());
        if (applied.actorId.trim()) query.set("actorId", applied.actorId.trim());
        if (applied.from) query.set("from", new Date(`${applied.from}T00:00:00`).toISOString());
        if (applied.to) query.set("to", new Date(`${applied.to}T23:59:59`).toISOString());
        if (before) query.set("before", before);
        const res = await fetch(`${API_BASE_URL}/admin/audit-logs?${query}`, {
          headers: getAdminHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          showError("Error", data.error || data.message || "Could not load the audit log.");
          return;
        }
        setRows((prev) => (before ? [...prev, ...data.data] : data.data));
        setHasMore(data.data.length === PAGE_SIZE);
      } catch {
        showError("Error", "Network error while loading the audit log.");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applied],
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.currentTarget.value }));
  const search = (e) => {
    e?.preventDefault();
    setApplied(filters);
  };
  const clear = () => {
    const empty = { action: "", actorId: "", from: "", to: "" };
    setFilters(empty);
    setApplied(empty);
  };

  return (
    <Stack gap="md" data-testid="audit-log-page">
      <div>
        <Title order={3}>Audit Log</Title>
        <Text c="dimmed" size="sm">
          Every change made by admins, moderators and shop owners, newest first. Failed attempts are
          included.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md" component="form" onSubmit={search}>
        <Group align="flex-end" wrap="wrap">
          <TextInput
            label="Action contains"
            placeholder="e.g. coupon, products, tickets"
            value={filters.action}
            onChange={setFilter("action")}
            leftSection={<IconSearch size={14} />}
            w={240}
          />
          <TextInput label="Person (user id)" placeholder="cuid" value={filters.actorId} onChange={setFilter("actorId")} w={220} />
          <TextInput label="From" type="date" value={filters.from} onChange={setFilter("from")} w={160} />
          <TextInput label="To" type="date" value={filters.to} onChange={setFilter("to")} w={160} />
          <Button type="submit">Search</Button>
          <Button variant="subtle" onClick={clear}>
            Clear
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="md" pos="relative" style={{ overflowX: "auto" }}>
        <LoadingOverlay visible={loading} />
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead bg="gray.0">
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th>Who</Table.Th>
              <Table.Th>Action</Table.Th>
              <Table.Th>Target</Table.Th>
              <Table.Th>Result</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center" py="lg">
                    No changes recorded for this search.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => (
              <Table.Tr key={row.id} data-testid="audit-row">
                <Table.Td>
                  <Text size="sm">{formatTime(row.createdAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {row.actor?.nickname || row.actor?.email || row.actorId || "Unknown"}
                  </Text>
                  <Group gap={4}>
                    {row.actorRole && (
                      <Badge size="xs" variant="light" color={ROLE_COLOR[row.actorRole] || "gray"}>
                        {row.actorRole}
                      </Badge>
                    )}
                    {row.actor?.email && (
                      <Text size="xs" c="dimmed">
                        {row.actor.email}
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {row.action}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace" c={row.targetId ? undefined : "dimmed"}>
                    {row.targetType ? `${row.targetType} ` : ""}
                    {row.targetId || "-"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={row.status} />
                  {row.reason && (
                    <Text size="xs" c="red" lineClamp={2} maw={260}>
                      {row.reason}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Button size="xs" variant="light" leftSection={<IconEye size={14} />} onClick={() => setSelected(row)}>
                    Details
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {hasMore && (
        <Group justify="center">
          <Button variant="default" loading={loading} onClick={() => load(rows[rows.length - 1]?.createdAt)}>
            Load more
          </Button>
        </Group>
      )}

      <Drawer opened={!!selected} onClose={() => setSelected(null)} position="right" size="lg" title="Change details">
        {selected && (
          <Stack gap="sm">
            <Text size="sm">
              <b>When:</b> {formatTime(selected.createdAt)}
            </Text>
            <Text size="sm">
              <b>Who:</b> {selected.actor?.nickname || "-"} ({selected.actor?.email || selected.actorId || "unknown"}),{" "}
              {selected.actorRole}
            </Text>
            <Text size="sm">
              <b>Action:</b> <Code>{selected.action}</Code>
            </Text>
            <Group gap="xs">
              <Text size="sm">
                <b>Result:</b>
              </Text>
              <StatusBadge status={selected.status} />
            </Group>
            {selected.reason && (
              <Text size="sm" c="red">
                <b>Reason:</b> {selected.reason}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              IP {selected.ip || "-"} · Request {selected.requestId || "-"}
            </Text>
            <JsonBlock label="Target (from the URL)" value={selected.params} />
            <JsonBlock label="What was sent (secrets removed)" value={selected.body} />
            <JsonBlock label="Before" value={selected.before} />
            <JsonBlock label="After" value={selected.after} />
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
